import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Button,
  Checkbox,
  CheckboxProps,
  Input,
  message,
  Popconfirm,
  Radio,
  Table,
  TableColumnsType,
  TableProps,
} from "antd";
import { IconActionKey, IconArrowDown, IconDownload2 } from "assets/svg";
import { TRANSACTION_HISTORY_FILTER_SORT } from "common/constants";
import { Filter, Transaction } from "common/interfaces";
import PaginateListMobile from "components/PaginateListMobile";
import { Link, useLocation, useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import { parseErrorMessage } from "common/utils";
import CustomBreadCrumb from "components/CustomBreadCrumb";
import { usePagination } from "common/hooks/usePagination";
import {
  useDeleteTransactionMutation,
  useDownloadTransactionCsvMutation,
  useGetTransactionsQuery,
} from "reducers/transactionApi";
import {
  TRANSACTION_HISTORY_FILTER_STATUS_TYPE,
  TransactionStatusEnum,
  TransactionStatusLabel,
} from "common/enums/TransactionStatus.enum";
import { UTCToJapanTime } from "common/helper/time.helper";
import CompanyTransactionItem from "components/CompanyTransactionItem";
import { UserTypeEnum } from "common/enums/UserType.enum";
import { PAGINATION } from "common/enums/Pagination.enum";
import { useCommonContext } from "contexts/CommonContext";

const CompanyTransactionListPage = () => {
  const { setHeaderTitle } = useCommonContext();
  const { companyId } = useParams<{ companyId?: string }>();

  const columns: TableColumnsType<Transaction> = [
    {
      title: "取引ID",
      dataIndex: "id",
      align: "center",
      render: (value: number, record: Transaction) => (
        <Link
          to={`/admin/company-management/${companyId}/transaction/${record.code}/view`}
          className="text-primary"
        >
          {record.code}
        </Link>
      ),
    },
    {
      title: "出品ID",
      dataIndex: "product",
      align: "center",
      render: (value, record: Transaction) => (
        <Link
          to={`/admin/company-management/${companyId}/product/${record.product.code}/view`}
          className="text-primary"
        >
          {record.product.code}
        </Link>
      ),
    },
    {
      title: "商品名",
      dataIndex: "product",
      align: "center",
      render: (value) => value.product_name,
    },
    {
      title: "売り手企業",
      dataIndex: "seller",
      align: "center",
      render: (value) => value.company_name,
    },
    {
      title: "買い手企業",
      dataIndex: "buyer",
      align: "center",
      render: (value) => value.company_name,
    },
    {
      title: "ステータス",
      dataIndex: "status",
      align: "center",
      render: (value: TransactionStatusEnum) => TransactionStatusLabel[value],
    },
    {
      title: "最終更新",
      dataIndex: "created_at",
      align: "center",
      render: (value) => {
        const timeJapan = UTCToJapanTime(value);
        return `${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`;
      },
    },
    {
      title: "操作",
      dataIndex: "id",
      align: "center",
      render: (value: number, record: Transaction) => (
        <>
          <Link
            to={`/admin/company-management/${companyId}/transaction/${record.code}/view`}
            className="text-primary"
          >
            詳細
          </Link>
        </>
      ),
    },
  ];
  const location = useLocation();
  const companyCode = location.state?.companyCode;
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const typeParam = query.get("type");
  const type = typeParam ? Number(typeParam) : null;
  const [showFilter, setShowFilter] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRowKeyMobile, setSelectedRowKeyMobile] = useState<string[]>(
    [],
  );
  const [searchValueDebounced, setSearchValueDebounced] = useState("");
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const {
    pagination,
    paginationMobile,
    onChangePage,
    reset: resetPagination,
    resetMobile: resetPaginationMobile,
    updateTotal,
    updateTotalMobile,
  } = usePagination();

  const defaultValues: Filter = {
    search: "",
    status: [
      TRANSACTION_HISTORY_FILTER_STATUS_TYPE.UNDER_NEGOTIATION.value,
      TRANSACTION_HISTORY_FILTER_STATUS_TYPE.WAIT_SHIP.value,
      TRANSACTION_HISTORY_FILTER_STATUS_TYPE.RECEIPT_COMPLETE.value,
      TRANSACTION_HISTORY_FILTER_STATUS_TYPE.REQUEST.value,
      TRANSACTION_HISTORY_FILTER_STATUS_TYPE.TRANSACTION_COMPLETE.value,
      TRANSACTION_HISTORY_FILTER_STATUS_TYPE.CANCEL.value,
    ],
    sort: TRANSACTION_HISTORY_FILTER_SORT.FINAL_UPDATE.value,
  };

  const { handleSubmit, control, watch, reset } = useForm<Filter>({
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [companyId, reset]);

  const transactionList: Filter = watch();
  const prevTransactionList = useRef<Filter>(defaultValues);

  const {
    data: transactionData,
    error: companyErr,
    refetch: refetchCompany,
  } = useGetTransactionsQuery(
    {
      filter: {
        ...transactionList,
        search: searchValueDebounced,
        buyer_id:
          type === UserTypeEnum.COMPANY_BUYER && companyId
            ? Number(companyId)
            : null,
        seller_id:
          type === UserTypeEnum.COMPANY_SELLER && companyId
            ? Number(companyId)
            : null,
      },
      page: pagination.current,
    },
    {
      skip: !companyId,
    },
  );

  const {
    data: transactionMobileData,
    error: transactionMobileErr,
    refetch: refetchCompanyMobile,
  } = useGetTransactionsQuery(
    {
      filter: {
        ...transactionList,
        search: searchValueDebounced,
        buyer_id:
          type === UserTypeEnum.COMPANY_BUYER && companyId
            ? Number(companyId)
            : null,
        seller_id:
          type === UserTypeEnum.COMPANY_SELLER && companyId
            ? Number(companyId)
            : null,
      },
      page: paginationMobile.current,
    },
    {
      skip: !companyId,
    },
  );

  const [deleteTransaction] = useDeleteTransactionMutation();
  const [downloadTransactionCsv] = useDownloadTransactionCsvMutation();

  const plainStatusOptions = Object.values(
    TRANSACTION_HISTORY_FILTER_STATUS_TYPE,
  ).map((o) => o.value);

  const [checkedStatusList, setCheckedStatusList] =
    useState<string[]>(plainStatusOptions);

  const checkAllStatus = plainStatusOptions.length === checkedStatusList.length;
  const indeterminateStatus =
    checkedStatusList.length > 0 &&
    checkedStatusList.length < plainStatusOptions.length;

  const toggleFilter = () => setShowFilter((prev) => !prev);

  const rowSelection: TableProps<Transaction>["rowSelection"] = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({ name: record.code }),
    hideSelectAll: true,
  };

  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      const allKeys =
        transactionData?.data?.items?.map((item) => item.code) ?? [];
      setSelectedRowKeys(allKeys);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleCheckAllMobile = (checked: boolean) => {
    if (checked) {
      setSelectedRowKeyMobile(
        transactionMobileData?.data?.items.map((item) => item.code) || [],
      );
    } else {
      setSelectedRowKeyMobile([]);
    }
  };

  const downloadCsv = async () => {
    await downloadTransactionCsv({
      filter: transactionList,
      fileName: "ユーザー管理.csv",
    });
  };

  const confirm = async () => {
    try {
      const keys = !isEmpty(selectedRowKeys)
        ? selectedRowKeys
        : !isEmpty(selectedRowKeyMobile)
          ? selectedRowKeyMobile
          : [];
      if (!isEmpty(keys)) {
        await deleteTransaction(keys).unwrap();
        message.success("削除に成功しました");
        resetPagination();
        resetPaginationMobile();
      }
    } catch (err) {
      message.error(parseErrorMessage(err));
    }
  };

  const onReset = async () => {
    resetPagination();
    resetPaginationMobile();
    reset();
    setSearchValueDebounced("");
    setCheckedStatusList(plainStatusOptions);
  };

  const onSubmit = async () => {
    resetPagination();
    resetPaginationMobile();
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    setSearchValueDebounced(transactionList.search);
    setTimeout(() => {
      refetchCompany();
      refetchCompanyMobile();
    }, 0);
  };

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearchValueDebounced(transactionList.search);
    }, 2000);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [transactionList.search]);

  useEffect(() => {
    if (
      JSON.stringify(prevTransactionList.current) !==
      JSON.stringify(transactionList)
    ) {
      resetPagination();
      resetPaginationMobile();
      prevTransactionList.current = transactionList;
    }
  }, [transactionList]);

  useEffect(() => {
    if (pagination.current === PAGINATION.PAGE) {
      refetchCompany();
    }
  }, [pagination.current]);

  useEffect(() => {
    if (paginationMobile.current === PAGINATION.PAGE) {
      refetchCompanyMobile();
    }
  }, [paginationMobile.current]);

  useEffect(() => {
    if (transactionData?.data?.meta?.total_items) {
      updateTotal(transactionData.data.meta.total_items);
    }
  }, [transactionData]);

  useEffect(() => {
    if (transactionMobileData?.data?.meta?.total_items) {
      updateTotalMobile(transactionMobileData.data.meta.total_items);
    }
  }, [transactionMobileData]);

  useEffect(() => {
    if (companyErr) void message.error(parseErrorMessage(companyErr));
  }, [companyErr]);

  useEffect(() => {
    if (transactionMobileErr)
      void message.error(parseErrorMessage(transactionMobileErr));
  }, [transactionMobileErr]);

  useEffect(() => {
    setHeaderTitle("取引履歴");
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full product-list">
      <div className="flex min-h-[752px] px-5 pt-5 md:p-[40px] pb-[100px] flex-col items-center gap-[30px] self-stretch">
        <div className="flex flex-col items-start gap-[10px] md:gap-[20px] self-stretch">
          <CustomBreadCrumb
            items={[
              {
                label: "ユーザー管理",
                link: "/admin/company-management",
              },
              {
                label: `ユーザー詳細（ID: #${companyCode}）`,
                link: `/admin/company-management/${companyCode}/view`,
              },
              {
                label: "取引履歴",
                isCurrent: true,
              },
            ]}
          />
          <Controller
            name="search"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                prefix={<IconActionKey />}
                placeholder="出品ID、商品名などで検索"
                className="w-full h-[60px] outline outline-[3px] outline-offset-[-3px] outline-primary"
              />
            )}
          />
          <div
            data-プロパティ1="base"
            className="inline-flex justify-start items-center cursor-pointer"
            onClick={toggleFilter}
          >
            <div className="justify-start text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
              検索条件を追加
            </div>
            <IconArrowDown className="w-[20px] h-[20px] aspect-[1/1]" />
          </div>
          {showFilter && (
            <div className="self-stretch p-[20px] md:p-10 bg-primary-light rounded-xl inline-flex flex-col justify-center items-center md:gap-7">
              <div className="self-stretch justify-start text-primary text-[18px] md:text-xl font-bold font-['Noto_Sans_JP'] leading-loose">
                検索条件（フィルター）
              </div>
              <div className="self-stretch flex flex-col justify-start items-start">
                <div className="self-stretch py-5 border-b border-white inline-flex flex-col md:flex-row justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    ステータス
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start">
                    <Controller
                      control={control}
                      name="status"
                      defaultValue={plainStatusOptions}
                      render={({ field }) => {
                        const onChange = (list: string[]) => {
                          setCheckedStatusList(list);
                          field.onChange(list);
                        };
                        const onCheckAllChange: CheckboxProps["onChange"] = (
                          e,
                        ) => {
                          const newList = e.target.checked
                            ? plainStatusOptions
                            : [];
                          setCheckedStatusList(newList);
                          field.onChange(newList);
                        };
                        return (
                          <>
                            <Checkbox
                              indeterminate={indeterminateStatus}
                              onChange={onCheckAllChange}
                              checked={checkAllStatus}
                            >
                              全て
                            </Checkbox>
                            <Checkbox.Group
                              {...field}
                              options={Object.values(
                                TRANSACTION_HISTORY_FILTER_STATUS_TYPE,
                              )}
                              value={checkedStatusList}
                              onChange={onChange}
                            />
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="self-stretch py-5 flex-col md:flex-row inline-flex justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    並び替え
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start">
                    <Controller
                      control={control}
                      name="sort"
                      render={({ field }) => (
                        <Radio.Group
                          {...field}
                          options={Object.values(
                            TRANSACTION_HISTORY_FILTER_SORT,
                          )}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="w-full md:w-auto flex-col-reverse md:flex-row inline-flex justify-start items-start gap-[10px] md:gap-5">
                <Button
                  block
                  type="primary"
                  htmlType="reset"
                  onClick={onReset}
                  className="min-w-full md:min-w-[340px] min-h-14 bg-white outline outline-2 outline-offset-[-2px] outline-primary text-primary text-lg font-bold"
                >
                  クリア
                </Button>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  className="min-w-full md:min-w-[340px] min-h-14 bg-primary text-lg font-bold"
                >
                  検索する
                </Button>
              </div>
            </div>
          )}
          <div className="self-stretch flex-col-reverse md:flex-row inline-flex justify-between items-center gap-3.5">
            <div className="hidden self-stretch md:inline-flex justify-start items-center gap-2.5">
              <Checkbox
                checked={
                  !!transactionData?.data?.items &&
                  selectedRowKeys.length ===
                    transactionData.data.items.length &&
                  transactionData.data.items.length > 0
                }
                indeterminate={
                  !!transactionData?.data?.items &&
                  selectedRowKeys.length > 0 &&
                  selectedRowKeys.length < transactionData.data.items.length
                }
                onChange={(e) => handleCheckAll(e.target.checked)}
              >
                全てにチェックを入れる
              </Checkbox>
            </div>
            <div className="md:hidden self-stretch inline-flex justify-start items-center gap-2.5">
              <Checkbox
                checked={
                  !!transactionMobileData?.data?.items &&
                  selectedRowKeyMobile.length ===
                    transactionMobileData.data.items.length &&
                  transactionMobileData.data.items.length > 0
                }
                indeterminate={
                  !!transactionMobileData?.data?.items &&
                  selectedRowKeyMobile.length > 0 &&
                  selectedRowKeyMobile.length <
                    transactionMobileData.data.items.length
                }
                onChange={(e) => handleCheckAllMobile(e.target.checked)}
              >
                全てにチェックを入れる
              </Checkbox>
            </div>
            <div className="flex flex-col-reverse md:flex-row gap-3.5">
              <Popconfirm
                title="タスクを削除する"
                description="このタスクを削除してもよろしいですか?"
                onConfirm={confirm}
                okText="はい"
                cancelText="いいえ"
                disabled={
                  isEmpty(selectedRowKeys) && isEmpty(selectedRowKeyMobile)
                }
              >
                <div
                  data-プロパティ1="text only"
                  className="flex justify-start items-center cursor-pointer"
                >
                  <div className="justify-start text-blue-900 text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
                    <>チェックした項目を削除</>
                  </div>
                </div>
              </Popconfirm>
              <Button
                type="primary"
                className="h-10 min-w-24 bg-primary"
                icon={
                  <IconDownload2 className="w-[20px] h-[20px] aspect-[1/1]" />
                }
                onClick={downloadCsv}
              >
                CSVデータをダウンロード
              </Button>
            </div>
          </div>
          <Table<Transaction>
            className="w-full hidden md:block"
            rowKey="code"
            rowSelection={rowSelection}
            onChange={onChangePage}
            pagination={
              !isEmpty(transactionData?.data?.items) ? pagination : false
            }
            columns={columns}
            dataSource={transactionData?.data?.items || []}
          />
          <PaginateListMobile<Transaction>
            selectedRowKeyMobile={selectedRowKeyMobile}
            setSelectedRowKeyMobile={setSelectedRowKeyMobile}
            pagination={
              !isEmpty(transactionData?.data?.items) ? paginationMobile : false
            }
            dataSource={transactionMobileData?.data?.items || []}
            renderItem={(item, checked, onSelect) => (
              <CompanyTransactionItem<Transaction>
                transaction={item}
                checked={checked}
                onSelect={onSelect}
              />
            )}
          />
        </div>
      </div>
    </form>
  );
};

export default CompanyTransactionListPage;
